// Mock-only routes added by the leads/pipeline/jobs/contacts slice. Signature:
// return a Response for a path you own, or null to fall through. These stand
// in for S11 (materials), the job notes feed, S9 homes and the message log
// until those reads exist on the live door; every figure is an example.
import * as fx from "./fixtures";
import { deriveMaterials } from "../components/job-materials";
import type { Home, InstallDay, Job, JobNote, Material, Message } from "../hooks/slice-a";

export type MockRoute = (path: string, q: URLSearchParams, method: string, init: RequestInit | undefined) => Promise<Response | null> | Response | null;

const ok = (data: unknown) => new Response(JSON.stringify({ ok: true, data }), { status: 200, headers: { "content-type": "application/json" } });
const NOW = new Date();
const iso = (dayOffset: number, hh: number, mm = 0) => { const d = new Date(NOW); d.setDate(d.getDate() + dayOffset); d.setHours(hh, mm, 0, 0); return d.toISOString(); };

/** The risk the desks know about, layered over the derived lines. */
function materialsFor(ref: string): Material[] {
  const job = fx.jobs.find((j) => j.ref === ref) as unknown as Job | undefined;
  if (!job) return [];
  const days = (fx.installs.filter((i) => i.job_ref === ref).flatMap((i) => i.days) as InstallDay[]) ?? [];
  const rows = deriveMaterials(job, days);
  if (ref === "J092610218") {
    const eta = iso(5, 0);
    for (const r of rows) {
      if (r.source.startsWith("PO")) { r.source = "PO-0081"; }
      if (/Fumed Oak|Underlay/.test(r.item)) { r.status = "at risk"; r.status_note = `ETA ${new Date(eta).toLocaleDateString("en-AU", { weekday: "short", day: "numeric" })} · the case at the top of this page`; }
      if (/Scotia/.test(r.item)) { r.status = "confirmed"; r.status_note = "ETA meets the day"; r.due = days[1]?.date ?? r.due; r.due_note = "day 2 · bedrooms"; }
    }
  }
  if (ref === "J092610231") {
    for (const r of rows) if (r.source.startsWith("PO")) { r.source = "PO-0082"; r.status = "not ordered"; r.status_note = "draft waits for approval · lead time 5 days"; }
  }
  return rows;
}

const NOTES: Record<string, JobNote[]> = {
  J092610218: [
    { at: iso(-1, 16, 10), who: "Jacky", text: "Priya is flexible next week if the planks slip; keep Thu 18 as first choice.", important: true },
    { at: iso(-3, 9, 30), who: "Winson", text: "Hall dip levelled and moisture test passed (11 %). Subfloor ready.", important: false },
    { at: iso(-6, 14, 0), who: "Jessie", text: "Deposit received by transfer. Sent the warranty statement with the receipt.", important: false },
  ],
  J092610231: [
    { at: iso(-1, 6, 30), who: "EmmA", text: "Draft PO-0082 written for 42 pk Smoked Oak; waiting for Jacky.", important: true },
    { at: iso(-2, 11, 0), who: "Jacky", text: "Chens confirmed the colour sample in the showroom.", important: false },
  ],
  J092610227: [{ at: iso(-7, 10, 0), who: "Jacky", text: "Quote sent by email; Daniel prefers phone but is hard to reach in the day.", important: false }],
  J092610210: [{ at: iso(0, 7, 10), who: "Jacky", text: "YU Flooring on site 7:10. Key safe code sent on the work order.", important: false }],
};

const HOMES: Record<string, Home[]> = {
  "c-0009": [{
    id: "h-0009-1", address: "14 Excelsior Ave, Castle Hill NSW 2154", measured_by: "Winson", measured_at: iso(-10, 10, 0),
    rooms: [
      { name: "Living / dining", w: 6.4, h: 5.2, m2: 33.3 }, { name: "Kitchen", w: 3.8, h: 3.6, m2: 13.7 }, { name: "Hall", w: 4.1, h: 1.2, m2: 4.9 },
      { name: "Bed 1", w: 4.2, h: 3.9, m2: 16.4 }, { name: "Bed 2", w: 3.6, h: 3.3, m2: 11.9 }, { name: "Bed 3", w: 3.4, h: 3.1, m2: 10.5 },
    ],
    total_m2: 84.2, notes: "Existing: tile in living, carpet in beds. 14 stairs, closed. Skirting 90 mm to remain; scotia.",
  }],
  "c-0006": [{
    id: "h-0006-1", address: "22 Rawson St, Epping NSW 2121", measured_by: "Winson", measured_at: iso(-14, 11, 0),
    rooms: [
      { name: "Living", w: 5.8, h: 4.6, m2: 26.7 }, { name: "Dining", w: 3.9, h: 3.4, m2: 13.3 }, { name: "Hall", w: 5.2, h: 1.1, m2: 5.7 },
      { name: "Bed 1", w: 3.6, h: 3.5, m2: 12.5 },
    ],
    total_m2: 58.2, notes: "Carpet throughout, to be removed. 14 stairs, closed, nosing per step. Hall dip to level.",
  }],
};

const MESSAGES: Record<string, Message[]> = {
  "c-0006": [
    { at: iso(-3, 14, 5), direction: "in", channel: "sms", who: "Priya Raman", text: "We are flexible next week if needed." },
    { at: iso(-3, 13, 40), direction: "out", channel: "sms", who: "Jacky", text: "Hi Priya, planks are due the 18th; we'll confirm the day by Friday." },
    { at: iso(-9, 10, 0), direction: "out", channel: "email", who: "Jacky", text: "Your Artemis Flooring quote J092610218 — Fumed Oak, stairs included." },
    { at: iso(-14, 12, 15), direction: "out", channel: "sms", who: "Winson", text: "On my way to 22 Rawson St for the measure, 15 min." },
  ],
  "c-0007": [
    { at: iso(-4, 9, 0), direction: "out", channel: "sms", who: "Jacky", text: "Hi Daniel, quote attached — happy to walk through it any evening." },
    { at: iso(-7, 10, 0), direction: "out", channel: "email", who: "Jacky", text: "Your Artemis Flooring quote J092610227 — Milano twist 'Pebble'." },
  ],
  "c-0009": [
    { at: iso(-2, 11, 20), direction: "in", channel: "wechat", who: "Wei Chen", text: "Smoked Oak it is. Thanks for the sample." },
    { at: iso(-2, 9, 0), direction: "out", channel: "wechat", who: "Jacky", text: "Sample of Smoked Oak vs Natural is in the showroom whenever suits." },
  ],
  "c-0005": [{ at: iso(-4, 11, 30), direction: "out", channel: "email", who: "Jessie", text: "Visit booked for Tuesday 10:00 with Winson. Reply if the time changes." }],
  "c-0004": [{ at: iso(-3, 9, 5), direction: "out", channel: "sms", who: "Jacky", text: "Hi Tom, Tuesday 14:30 for the measure at Victor St — Winson will text on the way." }],
};

export const sliceA: MockRoute = (path) => {
  let m = path.match(/^\/api\/mock\/job\/([^/]+)\/materials$/);
  if (m) return ok(materialsFor(m[1] ?? ""));
  m = path.match(/^\/api\/mock\/job\/([^/]+)\/notes$/);
  if (m) return ok(NOTES[m[1] ?? ""] ?? []);
  m = path.match(/^\/api\/mock\/contact\/([^/]+)\/homes$/);
  if (m) return ok(HOMES[m[1] ?? ""] ?? []);
  m = path.match(/^\/api\/mock\/contact\/([^/]+)\/messages$/);
  if (m) return ok(MESSAGES[m[1] ?? ""] ?? []);
  return null;
};
